/**
 * useSessionControl — Phase 11 client hook
 *
 * Subscribes to the authenticated user's session-control WebSocket channel
 * and dispatches incoming commands to the appropriate handlers:
 *  - lock_now            → portalSessionManager.lockPortal()
 *  - sign_out_everywhere → authService.logout() + redirect to /auth/login
 *  - session_terminated  → clear per-client offline data (if client_id) or
 *                          all offline data + authService.logout() + redirect
 *  - account_disabled    → clear ALL offline data + authService.logout() + redirect
 *  - device_wipe         → clear ALL offline data + authService.logout() + redirect
 *  - stay_alive          → reset inactivity timers via portalSessionManager
 *
 * The WebSocket is the primary delivery channel when the app is open
 * (instant, no FCM dependency). FCM is the fallback when the app is
 * backgrounded/closed. Cold-start session validation is the authoritative
 * security boundary if both pushes are missed.
 *
 * The hook is mounted inside SessionProvider so it only runs when the user
 * is authenticated and the session is being monitored. On unmount or logout
 * the subscription is cleaned up automatically by the underlying
 * useWebSocketChannel hook.
 */

'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { authService } from '@/lib/authService';
import { isSessionControlMessage, sessionControlChannel, type SessionControlMessage } from '@/lib/sessionControl';
import { wsClient } from '@/lib/websocket/TotalAccessWebSocket';

interface UseSessionControlOptions {
  /** Authenticated user id. When falsy, the hook is a no-op. */
  userId: number | null | undefined;
  /** Whether session monitoring is active (gates subscription). */
  enabled: boolean;
}

export function useSessionControl({ userId, enabled }: UseSessionControlOptions): void {
  const router = useRouter();
  // Keep a ref to the latest userId so the subscription effect can read it
  // without re-subscribing on every render.
  const userIdRef = useRef<number | null | undefined>(userId);
  userIdRef.current = userId;

  useEffect(() => {
    if (!enabled) return;
    const id = userIdRef.current;
    if (!id) return;

    const channel = sessionControlChannel(id);

    // Handler for incoming session-control channel messages.
    const handleMessage = async (data: unknown) => {
      if (!isSessionControlMessage(data)) return;
      const msg = data as SessionControlMessage;
      console.log('[useSessionControl] Received command:', msg.type, msg);

      // §29: install-targeted commands carry install_id — only the install
      // whose fingerprint matches acts; other installs/tabs ignore them.
      if ('install_id' in msg && msg.install_id) {
        const { getLocalInstallId } = await import('@/lib/installCommands');
        const localInstallId = await getLocalInstallId();
        if (localInstallId && msg.install_id !== localInstallId) {
          console.log('[useSessionControl] Command targets a different install — ignoring');
          return;
        }
      }

      switch (msg.type) {
        case 'lock_now': {
          // Lock the portal UI via portalSessionManager. The lock callback
          // bridge in useSessionManager will reflect the locked state into
          // React state and render PortalLockScreen.
          try {
            const { portalSessionManager } = await import('@/lib/portalSessionManager');
            portalSessionManager.lockPortal?.();
          } catch (err) {
            console.warn('[useSessionControl] lockPortal failed:', err);
          }
          break;
        }

        case 'session_terminated': {
          // Per-client or full termination. When client_id is present, clear
          // only that client's offline data (the user may have other active
          // memberships). When absent, clear ALL offline data.
          console.warn(`[useSessionControl] session_terminated: ${msg.reason || 'no reason provided'}`, 'client_id:', msg.client_id);
          try {
            const { clearAllOfflineDataForClient, clearAllOfflineData } = await import('@/lib/offline/clearClientData');
            if (msg.client_id) {
              console.log('[useSessionControl] Clearing offline data for client_id:', msg.client_id);
              await clearAllOfflineDataForClient(msg.client_id).catch((err) => {
                console.warn('[useSessionControl] Failed to clear per-client offline data:', err);
              });
            } else {
              console.log('[useSessionControl] Clearing ALL offline data (no client_id)');
              await clearAllOfflineData().catch((err) => {
                console.warn('[useSessionControl] Failed to clear all offline data:', err);
              });
            }
          } catch (err) {
            console.warn('[useSessionControl] Failed to import clearClientData:', err);
          }
          // §29: if the terminated client isn't this session's active client,
          // the session is still valid — wipe that client's data but keep the
          // user signed in (only its sessions for that client were revoked).
          if (msg.client_id && authService.getSessionClientId() !== msg.client_id) {
            console.log('[useSessionControl] Terminated client is not the active session client — staying signed in');
            break;
          }
          // Disconnect the WebSocket first to avoid a reconnect storm
          // after auth is cleared.
          try {
            wsClient.disconnect(true);
          } catch {
            // Non-critical
          }
          authService.emitSessionExpired(
            msg.reason || 'Your session was terminated by an administrator',
          );
          // Force navigation in case the sessionExpired listener is not active
          // on the current route (e.g. public pages).
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
            router.replace('/auth/login');
          }
          break;
        }

        case 'account_disabled':
        case 'device_wipe': {
          // Full account deactivation or company device wipe — clear ALL
          // offline data across all clients.
          console.warn(`[useSessionControl] ${msg.type}: ${msg.reason || 'no reason provided'}`);
          try {
            const { clearAllOfflineData } = await import('@/lib/offline/clearClientData');
            await clearAllOfflineData().catch((err) => {
              console.warn('[useSessionControl] Failed to clear all offline data:', err);
            });
          } catch (err) {
            console.warn('[useSessionControl] Failed to import clearClientData:', err);
          }
          try {
            wsClient.disconnect(true);
          } catch {
            // Non-critical
          }
          authService.emitSessionExpired(
            msg.reason || (msg.type === 'device_wipe'
              ? 'This device has been wiped by an administrator'
              : 'Your account has been disabled'),
          );
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
            router.replace('/auth/login');
          }
          break;
        }

        case 'sign_out_everywhere': {
          // Clear authentication state and redirect to login. We use
          // emitSessionExpired so the existing sessionExpired listener in
          // useSessionManager performs the full cleanup (clearAuth, redirect).
          console.warn(`[useSessionControl] sign_out_everywhere: ${msg.reason || 'no reason provided'}`);
          try {
            // Disconnect the WebSocket first to avoid a reconnect storm
            // after auth is cleared.
            wsClient.disconnect(true);
          } catch {
            // Non-critical
          }
          authService.emitSessionExpired(
            msg.reason || 'You have been signed out on all devices',
          );
          // Force navigation in case the sessionExpired listener is not active
          // on the current route (e.g. public pages).
          if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
            router.replace('/auth/login');
          }
          break;
        }

        case 'stay_alive': {
          // Reset inactivity timers. In later phases this will only apply to
          // TCA-managed devices; for now we always reset activity so a
          // managed-device heartbeat keeps the portal alive.
          try {
            const { portalSessionManager } = await import('@/lib/portalSessionManager');
            // recordExternalActivity resets the frontend inactivity timers
            // AND forwards a WS `activity` message to the server enforcer
            // (via the PORTAL_EXTERNAL_ACTIVITY_EVENT → useSessionManager
            // bridge). forceHeartbeat would only revalidate the session —
            // it does not reset inactivity.
            portalSessionManager.recordExternalActivity?.();
          } catch {
            // Non-critical
          }
          break;
        }
      }
    };

    // Subscribe via the low-level wsClient so we control cleanup precisely.
    // The wsClient resubscribes automatically on reconnect.
    const unsub = wsClient.subscribe(channel, handleMessage);

    return () => {
      unsub();
    };
  }, [enabled, router]);
}
